<template>
  <v-container align="center">
    <div v-if="chartData.datasets[0].data.length > 0">
      <div class="winnings metrics">
        <p>${{ chartStats.totalwinnings }} Won</p>
        <p>${{ chartStats.winningsperhour }} Won per Hour</p>
        <p v-if="chartStats.iscashgame">{{ chartStats.bigblindsperhour }} Big Blinds Won per Hour</p>
        <p>{{ chartStats.percentsessionswon }}% of Sessions Won </p>
      </div>
      <!-- Date Range Quick Select Buttons -->
      <div class="date-range-buttons" style="margin-bottom: 10px; display: flex; align-items: center; justify-content: center;">
        <v-btn small @click="setAllTime" class="mx-1" color="primary" variant="outlined" :active="dateMode === 'all'">All Time</v-btn>
        <v-btn small @click="setYearToDate" class="mx-1" color="primary" variant="outlined" :active="dateMode === 'ytd'">YTD</v-btn>
        <v-btn small @click="setLast12Months" class="mx-1" color="primary" variant="outlined" :active="dateMode === 'last12'">Last 12</v-btn>
        <v-btn small @click="setCustom" class="mx-1" color="primary" variant="outlined" :active="dateMode === 'custom'">Custom</v-btn>
      </div>
      <!-- X-axis unit override -->
      <div class="d-flex align-center justify-center flex-wrap mb-2" style="gap: 4px;">
        <span class="text-caption text-medium-emphasis mr-1">X-axis:</span>
        <v-btn
          v-for="opt in ['auto','day','week','month','quarter']"
          :key="opt"
          size="x-small"
          :variant="unitOverride === opt ? 'tonal' : 'text'"
          :color="unitOverride === opt ? 'primary' : undefined"
          @click="unitOverride = opt"
        >{{ opt === 'auto' ? `Auto (${autoUnit})` : opt }}</v-btn>
      </div>
      <!-- Custom date pickers -->
      <div v-if="dateMode === 'custom'" style="display: flex; align-items: center; justify-content: center; margin-bottom: 10px;">
        <v-menu
          v-model="fromDateMenu"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ props }">
            <v-text-field
              :model-value="filters.fromDate.date ? formatDate(filters.fromDate.date) : ''"
              @update:model-value="val => filters.fromDate.date = parseDate(val)"
              label="From"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="props"
              style="max-width: 200px; min-width: 160px; font-size: 1.2em; height: 56px; margin-left: 16px;"
              density="comfortable"
              hide-details
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="filters.fromDate.date"
            @input="fromDateMenu = false"
            :header-color="'primary'"
            :color="'primary'"
            show-adjacent-months
            :type="'date'"
          ></v-date-picker>
        </v-menu>
        <v-menu
          v-model="toDateMenu"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ props }">
            <v-text-field
              :model-value="filters.toDate.date ? formatDate(filters.toDate.date) : ''"
              @update:model-value="val => filters.toDate.date = parseDate(val)"
              label="To"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="props"
              style="max-width: 200px; min-width: 160px; font-size: 1.2em; height: 56px; margin-left: 16px;"
              density="comfortable"
              hide-details
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="filters.toDate.date"
            @input="toDateMenu = false"
            :header-color="'primary'"
            :color="'primary'"
            show-adjacent-months
            :type="'date'"
          ></v-date-picker>
        </v-menu>
      </div>
      <!-- Session Type Filter Buttons -->
      <div class="game-type-buttons" style="margin-bottom: 10px; display: flex; align-items: center; justify-content: center;">
        <v-btn small class="mx-1" color="primary" variant="outlined" :active="filters.typefilter === null" @click="setTypeFilter(null)">All</v-btn>
        <v-btn small class="mx-1" color="primary" variant="outlined" :active="filters.typefilter === CASH" @click="setTypeFilter(CASH)">Cash</v-btn>
        <v-btn small class="mx-1" color="primary" variant="outlined" :active="filters.typefilter === TOURNAMENT" @click="setTypeFilter(TOURNAMENT)">Tournament</v-btn>
      </div>
      <div class="chart" v-if="showgraph">
        <Line ref="linegraph" :data="chartData" :options="chartOptions" />
      </div>
      <!-- Additional Filters: Game, Place, Bet Type -->
      <div class="additional-filters" style="margin-top: 16px; display: flex; flex-wrap: wrap; align-items: flex-start; justify-content: center; gap: 12px;">
        <v-select
          v-model="filters.gamefilter"
          :items="gameslist"
          label="Game Played"
          multiple
          clearable
          chips
          density="compact"
          hide-details
          style="max-width: 220px; min-width: 160px;"
        ></v-select>
        <v-select
          v-model="filters.placefilter"
          :items="placelist"
          label="Place Played"
          multiple
          clearable
          chips
          density="compact"
          hide-details
          style="max-width: 220px; min-width: 160px;"
        ></v-select>
        <v-select
          v-model="filters.groupfilter"
          :items="placeGroups.map(g => g.name)"
          label="Place Group"
          multiple
          clearable
          chips
          density="compact"
          hide-details
          style="max-width: 220px; min-width: 160px;"
        ></v-select>
        <v-select
          v-model="filters.bettypefilter"
          :items="BET_TYPE_LIST_PICKER"
          label="Bet Type"
          multiple
          clearable
          chips
          density="compact"
          hide-details
          style="max-width: 220px; min-width: 160px;"
        ></v-select>
      </div>
    </div>
    <div v-else-if="sessions.length > 0">
      <h3>No Data</h3>
      <v-btn small color="primary" variant="outlined" @click="setAllTime">Show All Data</v-btn>
    </div>
    <div v-else>
      <h3>Enter your first poker Session</h3>
    </div>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { Line } from 'vue-chartjs';
import {
  Chart,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  TimeScale,
  Filler
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { useSessionStore } from '@/stores/sessions';
import { usePlaceStore } from '@/stores/places';
import { useGameStore } from '@/stores/games';
import { useUserStore } from '@/stores/users';
import { CASH, TOURNAMENT, BET_TYPE_LIST_PICKER } from '@/constants/constants';

const emit = defineEmits(['drilldown']);

// Register Chart.js components
Chart.register(
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  TimeScale,
  Filler
);

const GRAPH_SETTINGS_KEY = 'graphDisplaySettings';

const showgraph = ref(true);
const fromDateMenu = ref(false);
const toDateMenu = ref(false);
const dateMode = ref('all');
const unitOverride = ref('auto');

const filters = ref({
  gamefilter: [],
  typefilter: null,
  fromDate: { date: new Date(2019, 8, 1) },
  toDate: { date: new Date() },
  placefilter: [],
  bettypefilter: [],
  groupfilter: []
});

function saveSettings() {
  try {
    localStorage.setItem(GRAPH_SETTINGS_KEY, JSON.stringify({
      dateMode: dateMode.value,
      unitOverride: unitOverride.value,
      filters: {
        gamefilter: filters.value.gamefilter,
        typefilter: filters.value.typefilter,
        fromDate: filters.value.fromDate.date ? filters.value.fromDate.date.toISOString() : null,
        toDate: filters.value.toDate.date ? filters.value.toDate.date.toISOString() : null,
        placefilter: filters.value.placefilter,
        bettypefilter: filters.value.bettypefilter,
        groupfilter: filters.value.groupfilter
      }
    }));
  } catch (e) { /* ignore */ }
}

function loadSettings() {
  try {
    const saved = localStorage.getItem(GRAPH_SETTINGS_KEY);
    if (!saved) return;
    const s = JSON.parse(saved);
    dateMode.value = s.dateMode || 'all';
    unitOverride.value = s.unitOverride || 'auto';
    if (s.filters) {
      filters.value.gamefilter = s.filters.gamefilter || [];
      filters.value.typefilter = s.filters.typefilter ?? null;
      filters.value.fromDate.date = s.filters.fromDate ? new Date(s.filters.fromDate) : new Date(2019, 8, 1);
      filters.value.toDate.date = s.filters.toDate ? new Date(s.filters.toDate) : new Date();
      filters.value.placefilter = s.filters.placefilter || [];
      filters.value.bettypefilter = s.filters.bettypefilter || [];
      filters.value.groupfilter = s.filters.groupfilter || [];
    }
  } catch (e) { /* ignore */ }
}

const autoUnit = computed(() => {
  const from = filters.value.fromDate?.date;
  const to   = filters.value.toDate?.date;
  if (!from || !to) return 'day';
  const days = Math.abs((new Date(to) - new Date(from)) / 86400000);
  if (days <= 90)  return 'day';
  if (days <= 365) return 'week';
  if (days <= 730) return 'month';
  return 'quarter';
});

const activeUnit = computed(() =>
  unitOverride.value === 'auto' ? autoUnit.value : unitOverride.value
);

function getBucketKey(date, unit) {
  const d = new Date(date);
  if (unit === 'week') {
    d.setDate(d.getDate() - d.getDay());
  } else if (unit === 'month') {
    d.setDate(1);
  } else if (unit === 'quarter') {
    d.setMonth(Math.floor(d.getMonth() / 3) * 3, 1);
  }
  d.setHours(0, 0, 0, 0);
  return d.toISOString().split('T')[0];
}

function getBucketEndKey(startKey, unit) {
  const d = new Date(startKey + 'T00:00:00');
  if (unit === 'week') {
    d.setDate(d.getDate() + 6);
  } else if (unit === 'month') {
    d.setMonth(d.getMonth() + 1);
    d.setDate(d.getDate() - 1);
  } else if (unit === 'quarter') {
    d.setMonth(d.getMonth() + 3);
    d.setDate(d.getDate() - 1);
  }
  return d.toISOString().split('T')[0];
}

const sessionStore = useSessionStore();
const placeStore = usePlaceStore();
const gameStore = useGameStore();
const userStore = useUserStore();
const placeGroups = computed(() => userStore.placeGroups);

const sessionlist = computed(() => sessionStore.sessionlist);
const sessions = computed(() => sessionStore.sessionlist);
const placelist = computed(() => placeStore.placelist);
const gameslist = computed(() => gameStore.gameslist);

const filteredsessionlist = computed(() => {
  const f = filters.value;
  const fromDate = (f && f.fromDate && f.fromDate.date) ? new Date(f.fromDate.date) : new Date(2019, 8, 1);
  const toDate = (f && f.toDate && f.toDate.date) ? new Date(f.toDate.date) : new Date();
  const gamefilter = Array.isArray(f.gamefilter) ? f.gamefilter : [];
  const typefilter = f.typefilter;
  const placefilter = Array.isArray(f.placefilter) ? f.placefilter : [];
  const bettypefilter = Array.isArray(f.bettypefilter) ? f.bettypefilter : [];
  const groupfilter = Array.isArray(f.groupfilter) ? f.groupfilter : [];

  const groupPlaces = new Set();
  groupfilter.forEach(name => {
    const g = placeGroups.value.find(g => g.name === name);
    if (g) g.places.forEach(p => groupPlaces.add(p));
  });
  const placeActive = placefilter.length > 0 || groupPlaces.size > 0;

  return Array.isArray(sessionStore.sessionlist)
    ? sessionStore.sessionlist.filter(session => {
        if (!session || !session.sessiondate) return false;
        const sessionDate = session.sessiondate instanceof Date ? session.sessiondate : new Date(session.sessiondate);
        return (
          sessionDate >= fromDate &&
          sessionDate.setHours(0, 0, 0, 0) <= toDate.setHours(0, 0, 0, 0) &&
          (!gamefilter.length || gamefilter.includes(session.game)) &&
          (!typefilter || typefilter === session.type) &&
          (!placeActive || placefilter.includes(session.place) || groupPlaces.has(session.place)) &&
          (!bettypefilter.length || bettypefilter.includes(session.bettype))
        );
      })
    : [];
});

const chartStats = computed(() => {
  let summarysessiondata = {};
  let totallength = 0;
  let mindate = null;
  let maxdate = null;
  let bigblindswon = 0;
  let bigblindstime = 0;
  let totalwinnings = 0;
  let winningsessions = 0;
  let losingsessions = 0;
  let evensessions = 0;

  filteredsessionlist.value.forEach(item => {
    const dateKey = getBucketKey(item.sessiondate, activeUnit.value);
    if (summarysessiondata[dateKey]) {
      summarysessiondata[dateKey] += item.net;
    } else {
      summarysessiondata[dateKey] = item.net;
    }
    mindate = (!mindate || mindate > item.sessiondate) ? item.sessiondate : mindate;
    maxdate = (!maxdate || maxdate < item.sessiondate) ? item.sessiondate : maxdate;
    totallength += item.length;
    if (item.net > 0) {
      winningsessions += 1;
    } else {
      if (item.net === 0) {
        evensessions += 1;
      } else {
        losingsessions += 1;
      }
    }
    totalwinnings += item.net;
    if (item.bigblind && item.bigblind > 0 && item.length > 0) {
      bigblindswon += (item.net / item.bigblind);
      bigblindstime += item.length;
    }
  });

  const labels = Object.keys(summarysessiondata).sort();
  const data = labels.map(date => summarysessiondata[date]);

  const winningsperhour = (totallength === 0) ? 0 : Number.parseFloat(totalwinnings / totallength).toFixed(2);
  const bigblindsperhour = (bigblindstime === 0) ? 0 : Number.parseFloat(bigblindswon / bigblindstime).toFixed(2);
  let totalsessions = winningsessions + losingsessions + evensessions;
  const percentsessionswon = (totalsessions > 0) ? Number.parseFloat(winningsessions / totalsessions * 100).toFixed(2) : 0;
  const iscashgame = (bigblindstime === 0) ? false : true;

  return {
    labels,
    data,
    totalwinnings: totalwinnings.toFixed(2),
    winningsperhour,
    bigblindsperhour,
    percentsessionswon,
    iscashgame
  };
});

const chartData = computed(() => ({
  labels: chartStats.value.labels,
  datasets: [
    {
      label: 'Net Winnings',
      data: chartStats.value.data,
      borderColor: '#8e24aa',
      backgroundColor: 'rgba(142,36,170,0.1)',
      tension: 0.2,
      pointRadius: 3,
      fill: true,
    }
  ]
}));

const tooltipFormatByUnit = { day: 'PP', week: 'PP', month: 'MMM yyyy', quarter: 'MMM yyyy' };
const displayFormatsByUnit = {
  day:     { day: 'MMM d' },
  week:    { week: 'MMM d' },
  month:   { month: 'MMM yyyy' },
  quarter: { month: 'MMM yyyy' },
};

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  onClick: (event, elements, chart) => {
    if (elements.length > 0) {
      const index  = elements[0].index;
      const label  = chart.data.labels[index]; // YYYY-MM-DD bucket start
      const unit   = activeUnit.value;
      const endKey = getBucketEndKey(label, unit);
      emit('drilldown', {
        fromDate:     { date: new Date(label + 'T00:00:00') },
        toDate:       { date: new Date(endKey + 'T00:00:00') },
        typefilter:   filters.value.typefilter,
        gamefilter:   [...(filters.value.gamefilter   || [])],
        placefilter:  [...(filters.value.placefilter  || [])],
        bettypefilter: [...(filters.value.bettypefilter || [])]
      });
    }
  },
  onHover: (event, elements) => {
    if (event.native?.target) {
      event.native.target.style.cursor = elements.length ? 'pointer' : 'default';
    }
  },
  plugins: {
    legend:  { display: true, position: 'top' },
    title:   { display: true, text: 'Session Performance' },
    tooltip: { mode: 'index', intersect: false }
  },
  scales: {
    x: {
      type: 'time',
      time: {
        unit:           activeUnit.value,
        tooltipFormat:  tooltipFormatByUnit[activeUnit.value],
        displayFormats: displayFormatsByUnit[activeUnit.value],
      },
      title: { display: true, text: 'Date' }
    },
    y: {
      title: { display: true, text: 'Winnings' },
      ticks: { callback: value => `$${value}` }
    }
  }
}));

function setAllTime() {
  if (!sessionlist.value.length) return;
  const sorted = [...sessionlist.value].sort((a, b) => new Date(a.sessiondate) - new Date(b.sessiondate));
  filters.value.fromDate.date = sorted[0]?.sessiondate ? new Date(sorted[0].sessiondate) : new Date(2019, 8, 1);
  filters.value.toDate.date = sorted[sorted.length - 1]?.sessiondate ? new Date(sorted[sorted.length - 1].sessiondate) : new Date();
  filters.value.gamefilter = [];
  filters.value.typefilter = null;
  filters.value.placefilter = [];
  filters.value.bettypefilter = [];
  filters.value.groupfilter = [];
  dateMode.value = 'all';
}

function setYearToDate() {
  const now = new Date();
  filters.value.fromDate.date = new Date(now.getFullYear(), 0, 1);
  filters.value.toDate.date = new Date();
  dateMode.value = 'ytd';
}

function setLast12Months() {
  const now = new Date();
  filters.value.fromDate.date = new Date(now.getFullYear(), now.getMonth() - 11, 1);
  filters.value.toDate.date = new Date();
  dateMode.value = 'last12';
}

function setCustom() {
  dateMode.value = 'custom';
}

function setTypeFilter(type) {
  filters.value.typefilter = type;
}

function formatDate(date) {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseDate(val) {
  if (!val) return null;
  return new Date(val + 'T00:00:00');
}

watch([dateMode, unitOverride, filters], saveSettings, { deep: true });

onMounted(() => {
  loadSettings();
});
</script>

<style scoped>
.winnings {
  font-size: 2vw;
  margin-top: 2vw;
  margin-bottom: 0vw;
  text-align: center;
}
.chart {
  width: 100%;
  max-width: 700px;
  height: 350px;
  margin: 0 auto;
  overflow-x: auto;
  box-sizing: border-box;
}
.date-range-buttons,
.game-type-buttons {
  flex-wrap: wrap;
  gap: 8px;
}
.additional-filters {
  max-width: 700px;
  margin: 0 auto;
}
@media (max-width: 600px) {
  .winnings {
    font-size: 1.1em;
    margin-top: 12px;
    margin-bottom: 0;
  }
  .chart {
    height: 300px;
    max-width: 100vw;
    min-width: 0;
    padding: 0 2vw;
  }
  .date-range-buttons,
  .game-type-buttons {
    flex-direction: column !important;
    align-items: stretch !important;
    justify-content: flex-start !important;
    gap: 6px;
  }
  .date-range-buttons > *,
  .game-type-buttons > * {
    width: 100%;
    min-width: 0;
    margin: 0;
  }
  .v-text-field {
    font-size: 1em !important;
    min-width: 120px !important;
    max-width: 100vw !important;
  }
  .additional-filters {
    flex-direction: column !important;
    align-items: stretch !important;
    justify-content: flex-start !important;
  }
  .additional-filters .v-select {
    max-width: 100% !important;
    min-width: 0 !important;
    width: 100% !important;
  }
}
</style>
